// Seeds the store-context Zustand slice from the resolved industry config,
// honouring .env overrides for local dev.
//
// Precedence (highest to lowest):
//   1. .env override (VITE_ACCOUNT_ID / VITE_CHANNEL_ID / VITE_LOCATION_ID)  ← highest
//   2. Industry config (accountId / channelId / defaultLocationId)
//   3. Existing persisted value (from a previous session — Zustand persist)
//   4. Empty
//
// Env overrides intentionally beat localStorage so that changing
// .env.development takes effect immediately without clearing storage.
// priceField is *always* taken from the active industry — that's a
// projection knob, not user state.
import { useEffect, useRef, useState } from 'react';
import { Typography } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import { loadMerchantConfig, storefrontHost } from '@core/platform';
import { useMerchantStore } from '@core/store/merchantStore';
import { useStoreContextStore } from '@core/store/storeContextStore';
import { resolveStoreByDomain } from '@core/services/storeResolveService';
import { getLocationBrandOrgSettings } from '@core/services/locationService';
import {
  getActiveAccount,
  normalizeResolvedAccounts,
  useAccountSelectionStore,
} from '@core/store/accountSelectionStore';
import { useRuntimeBrandStore } from '@core/store/runtimeBrandStore';
import { useRuntimeThemeStore } from '@core/store/runtimeThemeStore';
import {
  getContactDetails,
  getIndustryTheme,
  getPolicies,
  getSocialLinks,
} from '@core/settings/ecommerceSettings';
import {
  applyThemeCssVars,
  parseThemeSettings,
  toRuntimeThemeState,
} from '@core/settings/themeSettings';
import { applyOrgRuntime, getOrgIndustryTheme } from '@core/utils/accountRuntime';
import {
  getDomainBrandName,
  pickResolvedBrandName,
  pickResolvedLogoUrl,
  pickResolvedPoweredBy,
} from '@core/utils/runtimeBrand';
import { env } from '@config/env';
import AccountSelectionModal from '@components/base/AccountSelectionModal';
import i18n from '@/i18n';
import { useIndustry } from './industry-context.js';
const { Title, Text } = Typography;

const LANGUAGE_STORAGE_KEY = 'storefront-language';

function isAccountNotFoundError(error) {
  return (
    error?.status === 404 ||
    String(error?.code || '').toUpperCase() === 'NOT_FOUND' ||
    /account not found/i.test(String(error?.message || ''))
  );
}

function isAccountNotFoundResponse(response) {
  return (
    response?.success === false &&
    isAccountNotFoundError(response?.error || response?.errors || response)
  );
}

function AccountNotFoundScreen() {
  return (
    <div
      style={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        background: 'linear-gradient(180deg, #fffdfb 0%, #fff8f3 100%)',
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: -80,
          right: -70,
          width: 320,
          height: 320,
          borderRadius: '50%',
          background:
            'radial-gradient(circle at center, rgba(255, 190, 170, 0.20) 0%, rgba(255, 190, 170, 0.20) 42%, rgba(255, 190, 170, 0.08) 42%, rgba(255, 190, 170, 0.08) 58%, transparent 58%)',
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: -120,
          bottom: -120,
          width: 360,
          height: 360,
          borderRadius: '50%',
          background:
            'radial-gradient(circle at center, rgba(255, 190, 170, 0.22) 0%, rgba(255, 190, 170, 0.22) 48%, rgba(255, 190, 170, 0.08) 48%, rgba(255, 190, 170, 0.08) 62%, transparent 62%)',
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: '50%',
          bottom: -40,
          width: '120%',
          height: 180,
          transform: 'translateX(-50%)',
          backgroundImage:
            'repeating-radial-gradient(circle at 0 0, rgba(255, 188, 165, 0.12) 0 1px, transparent 1px 18px)',
          opacity: 0.45,
          maskImage: 'linear-gradient(180deg, transparent 0%, #000 35%, #000 100%)',
          WebkitMaskImage: 'linear-gradient(180deg, transparent 0%, #000 35%, #000 100%)',
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          right: 30,
          bottom: 42,
          width: 180,
          height: 180,
          backgroundImage:
            'radial-gradient(circle, rgba(255, 188, 165, 0.22) 2px, transparent 2px)',
          backgroundSize: '18px 18px',
          opacity: 0.55,
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: 40,
          top: 40,
          width: 88,
          height: 88,
          backgroundImage:
            'radial-gradient(circle, rgba(255, 188, 165, 0.22) 2px, transparent 2px)',
          backgroundSize: '16px 16px',
          opacity: 0.55,
        }}
      />
      <div
        style={{
          maxWidth: 560,
          width: '100%',
          textAlign: 'center',
          background: '#fff',
          padding: '44px 32px 36px',
          borderRadius: '24px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
          border: '1px solid #f1f1f1',
        }}
      >
        <div
          style={{
            width: 84,
            height: 84,
            margin: '0 auto 22px',
            borderRadius: '50%',
            background: '#fff2f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <span
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              border: '3px solid #ff5d5d',
              color: '#ff5d5d',
              display: 'grid',
              placeItems: 'center',
              fontSize: 28,
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            !
          </span>
        </div>

        <Title level={2} style={{ marginBottom: 10 }}>
          Account Not Found
        </Title>

        <Text
          type="secondary"
          style={{
            display: 'block',
            marginBottom: 30,
            fontSize: 16,
          }}
        >
          Please contact PosBytz support for assistance.
        </Text>

        <a
          href="https://help.posbytz.com/en/"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            minHeight: 42,
            padding: '0 18px',
            borderRadius: 10,
            background: '#ff8a00',
            color: '#fff',
            fontSize: 15,
            fontWeight: 600,
            textDecoration: 'none',
            // boxShadow: '0 8px 14px rgba(255, 138, 0, 0.2)',
          }}
        >
          <ExportOutlined style={{ fontSize: 15 }} />
          Visit PosBytz Help Center
        </a>
      </div>
    </div>
  );
}

function pickResolvedContext(resolved = {}) {
  return {
    accountId: resolved.account_details?.account_id || '',
    channelId: resolved.channel_id || '',
    locationId: resolved.locationId || resolved.location_id || resolved.location?.location_id || '',
  };
}

function pickResolvedBusinessAddress(resolved = {}) {
  const addr = resolved?.account_details?.address || {};
  if (typeof addr === 'string' && addr) return addr;
  const parts = [
    addr.address_line_1 || '',
    addr.address_line_2 || '',
    addr.city || '',
    addr.state || '',
    addr.zip_code || '',
  ].filter(Boolean);
  return parts.join(', ');
}

function pickResolvedBusinessPhone(resolved = {}) {
  const mob = resolved?.account_details?.mobile_number || {};
  const fromMobileObj =
    mob.number ||
    (mob.calling_code && mob.number ? `+${mob.calling_code} ${mob.number}` : '') ||
    mob.full_number ||
    '';
  return fromMobileObj || '';
}

function pickResolvedBusinessEmail(resolved = {}) {
  return getContactDetails(resolved?.account_details?.settings?.ecommerce_settings, {
    fallbackEmail: resolved?.account_details?.email,
  }).email;
}

function pickResolvedSupportPhone(resolved = {}) {
  return getContactDetails(resolved?.account_details?.settings?.ecommerce_settings).supportPhone;
}

function pickResolvedWhatsappNumber(resolved = {}) {
  return getContactDetails(resolved?.account_details?.settings?.ecommerce_settings).whatsappNumber;
}

function pickResolvedSocialLinks(resolved = {}) {
  return getSocialLinks(resolved?.account_details?.settings?.ecommerce_settings);
}

function pickResolvedCountry(resolved = {}) {
  return resolved?.account_details?.preferences?.country_code || '';
}

function pickResolvedLanguage(resolved = {}) {
  return resolved?.account_details?.preferences?.language || '';
}

function normalizeCurrencyConfig(input) {
  if (!input || typeof input !== 'object') return null;
  const code = input.code || '';
  const symbol = input.symbol || '';
  const precision = input.precision ?? undefined;

  if (!code && !symbol) return null;

  return {
    code,
    symbol,
    precision: Number.isFinite(Number(precision)) ? Number(precision) : 2,
    rounding_method: input.rounding_method || input.roundingMethod || '',
  };
}

function pickResolvedCurrency(resolved = {}) {
  return normalizeCurrencyConfig(resolved?.account_details?.preferences?.currency) || '';
}

function normalizeLanguage(input) {
  return String(input || 'en')
    .toLowerCase()
    .split('-')[0];
}

function applyDocumentLanguage(language) {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = language;
  document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
}

function pickResolvedIndustryTheme(resolved = {}) {
  return getIndustryTheme(resolved?.account_details?.settings?.ecommerce_settings);
}

function pickResolvedTheme(resolved = {}) {
  const parsed = parseThemeSettings(resolved?.account_details?.settings?.theme_settings, {
    logoFallbacks: [pickResolvedLogoUrl(resolved)],
  });
  return toRuntimeThemeState(parsed, resolved?.account_details?.settings?.ecommerce_settings || {});
}

export default function StoreContextBootstrap({ children }) {
  const { config, setIndustry } = useIndustry();
  const bootstrapConfigRef = useRef(config);
  const [bootstrapState, setBootstrapState] = useState('loading');
  // Flagship builds switch merchant at runtime; re-resolving is what rebrands
  // the app. Always '' on web and in dedicated builds, so this never re-runs
  // there. See ADR 0005.
  const selectedMerchantDomain = useMerchantStore((s) => s.selectedDomain);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      let accountNotFound = false;
      try {
        const current = useStoreContextStore.getState();
        const bootstrapConfig = bootstrapConfigRef.current;
        const fallback = {
          accountId: current.accountId || env.accountIdOverride || bootstrapConfig.accountId || '',
          channelId: current.channelId || env.channelIdOverride || bootstrapConfig.channelId || '',
          // Do not auto-select a default location from config.
          // Keep location empty unless restored from existing state/env or resolved explicitly.
          locationId: current.locationId || env.locationIdOverride || '',
        };

        let resolved = null;
        try {
          // Native reads its merchant identity from a generated merchant.json
          // alongside the bundle, so this must land before the host is read.
          // No-op on web. See ADR 0005.
          await loadMerchantConfig();
          const domain = storefrontHost();
          if (domain) {
            resolved = await resolveStoreByDomain({ domain });
          }

          if (isAccountNotFoundResponse(resolved)) {
            accountNotFound = true;
            return;
          }
        } catch (error) {
          if (isAccountNotFoundError(error)) {
            accountNotFound = true;
          }
        }
        if (cancelled) return;
        if (accountNotFound) return;

        const resolvedContext = pickResolvedContext(resolved || {});
        const resolvedLanguage = normalizeLanguage(pickResolvedLanguage(resolved || '') || 'en');
        const resolvedCurrency = pickResolvedCurrency(resolved || {});
        const resolvedIndustryTheme = pickResolvedIndustryTheme(resolved || {});
        const resolvedTheme = pickResolvedTheme(resolved || {});
        const resolvedAccounts = normalizeResolvedAccounts(resolved || {});
        const resolvedBrandName =
          pickResolvedBrandName(resolved || {}) ||
          getDomainBrandName(window.location?.hostname) ||
          bootstrapConfig?.copy?.appName ||
          bootstrapConfig?.name ||
          '';
        const resolvedAddress = pickResolvedBusinessAddress(resolved || '');
        const resolvedPhone = pickResolvedBusinessPhone(resolved || '');
        const resolvedSocialLinks = pickResolvedSocialLinks(resolved || '');
        const resolvedEmail = pickResolvedBusinessEmail(resolved || '');
        const resolvedSupportPhone = pickResolvedSupportPhone(resolved || '');
        const resolvedWhatsappNumber = pickResolvedWhatsappNumber(resolved || '');
        const {
          privacyPolicy: resolvedPrivacyPolicy,
          termsAndConditions: resolvedTermsAndConditions,
          aboutUs: resolvedAboutUs,
        } = getPolicies(resolved?.account_details?.settings?.ecommerce_settings);
        const resolvedPoweredBy = pickResolvedPoweredBy(resolved || {}, {
          sourceHost: storefrontHost(),
        });

        applyThemeCssVars(resolvedTheme);
        if (resolvedIndustryTheme) {
          setIndustry(resolvedIndustryTheme);
        }
        useRuntimeThemeStore.getState().setThemeColors(resolvedTheme);
        useRuntimeBrandStore.getState().setLogoUrl(resolvedTheme.logoUrl || '/pwa-192x192.png');
        useRuntimeBrandStore.getState().setBrandName(resolvedBrandName);
        useRuntimeBrandStore.getState().setBusinessAddress(resolvedAddress);
        useRuntimeBrandStore.getState().setBusinessPhone(resolvedPhone);
        useRuntimeBrandStore.getState().setBusinessEmail(resolvedEmail);
        useRuntimeBrandStore.getState().setSupportPhone(resolvedSupportPhone);
        useRuntimeBrandStore.getState().setWhatsappNumber(resolvedWhatsappNumber);
        useRuntimeBrandStore.getState().setPrivacyPolicy(resolvedPrivacyPolicy);
        useRuntimeBrandStore.getState().setTermsAndConditions(resolvedTermsAndConditions);
        useRuntimeBrandStore.getState().setAboutUs(resolvedAboutUs);
        useRuntimeBrandStore.getState().setPoweredBy(resolvedPoweredBy);
        if (resolvedSocialLinks) {
          useRuntimeBrandStore.getState().setSocialLinks(resolvedSocialLinks);
        }

        useAccountSelectionStore.getState().setResolvedAccounts(resolvedAccounts);

        // Language precedence:
        // 1. User-selected language persisted in local storage
        // 2. Merchant default from resolve API (preferences.language)
        let nextLanguage = resolvedLanguage;
        try {
          const storedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
          if (storedLanguage) {
            nextLanguage = normalizeLanguage(storedLanguage);
          } else if (resolvedLanguage) {
            window.localStorage.setItem(LANGUAGE_STORAGE_KEY, resolvedLanguage);
          }
        } catch {
          // Ignore storage access issues and continue with resolved/default language.
        }
        i18n.changeLanguage(nextLanguage);
        applyDocumentLanguage(nextLanguage);

        const searchParams = new URLSearchParams(window.location.search);
        const urlAccountId = searchParams.get('account');
        const urlLocationId = searchParams.get('location');
        const urlAccount = urlAccountId
          ? resolvedAccounts.availableAccounts.find((account) => account.id === urlAccountId)
          : null;

        const currentSelection = getActiveAccount();
        const primaryAccount = resolvedAccounts.primaryAccount;
        const selectedAccount =
          urlAccount ||
          (currentSelection &&
          resolvedAccounts.availableAccounts.some((account) => account.id === currentSelection.id)
            ? currentSelection
            : null);
        const hasLinkedAccounts = resolvedAccounts.availableAccounts.length > 1;
        const nextAccount =
          selectedAccount || primaryAccount || resolvedAccounts.availableAccounts[0] || null;

        const nextLocationId = urlLocationId || current.locationId || '';
        const persistedBrandId =
          !urlLocationId || urlLocationId === current.locationId ? current.brandId : '';

        useStoreContextStore.getState().hydrate({
          accountId: nextAccount?.id || resolvedContext.accountId || fallback.accountId,
          channelId: resolvedContext.channelId || fallback.channelId,
          locationId: nextLocationId,
          accountCountry: pickResolvedCountry(resolved || {}),
          priceField: bootstrapConfig.priceField || 'online_price',
          currencyConfig: resolvedCurrency || current.currencyConfig,
        });

        if (nextAccount) {
          if (hasLinkedAccounts && !urlAccount && !currentSelection) {
            useAccountSelectionStore.getState().openAccountSelector();
          } else {
            useAccountSelectionStore
              .getState()
              .setActiveAccount(nextAccount, { resetLocation: false });
          }
        }

        if (nextLocationId && persistedBrandId) {
          try {
            const orgSettings = await getLocationBrandOrgSettings({
              locationId: nextLocationId,
              brandId: persistedBrandId,
            });
            if (cancelled) return;
            if (orgSettings) {
              applyOrgRuntime(orgSettings);
              setIndustry(getOrgIndustryTheme(orgSettings));
            }
          } catch {
            // Keep the resolved account runtime if persisted brand settings cannot be reloaded.
          }
        }
      } finally {
        if (!cancelled) {
          setBootstrapState(accountNotFound ? 'not_found' : 'ready');
        }
      }
    }

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, [setIndustry, selectedMerchantDomain]);

  if (bootstrapState === 'loading') {
    return null;
  }

  if (bootstrapState === 'not_found') {
    return <AccountNotFoundScreen />;
  }

  return (
    <>
      <AccountSelectionModal />
      {children}
    </>
  );
}
